import { useEffect } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader } from '@lms/ui';
import { H5pPlayer } from '@/components/h5p/H5pPlayer';
import type { H5pPlayerPayload } from '@/components/h5p/types';
import { useSectionItemCompletion } from '@/hooks/useSectionItemCompletion';
import { AssessmentPlayer } from '@/components/assessments/AssessmentPlayer';
import { AssessmentStaffAttemptsView } from '@/components/assessments/AssessmentStaffAttemptsView';
import { HubDetailPageHeader } from '@/components/layout/HubDetailPageHeader';
import {
  LearningObjectPlayer,
  type LearningObjectPlayContent,
} from '@/components/learning/LearningObjectPlayer';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { coursePlayBackUrl, studentCourseBackUrl } from '@/lib/course-admin-navigation';
import { useLearningSessionContext } from '@/providers/learning-session-context';

interface PlayPayload {
  item: { id: string; title: string; itemType?: string; type?: string };
  player?: H5pPlayerPayload | null;
  content?: LearningObjectPlayContent | null;
}

function isH5pPlayerPayload(value: unknown): value is H5pPlayerPayload {
  return Boolean(value && typeof value === 'object' && 'available' in value);
}

export function CoursePlayPage() {
  const { id, item } = useParams<{ id: string; item: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const isStaffAssessmentView = hasPermission(PERMISSIONS.EVALUACIONES_VER);
  const isAdminPreview = location.pathname.startsWith('/courses/');

  const playUrl = isAdminPreview
    ? `/courses/${id}/items/${item}/play`
    : `/student/courses/${id}/items/${item}/play`;
  const backUrl = isAdminPreview && id
    ? coursePlayBackUrl(id, searchParams)
    : id
      ? studentCourseBackUrl(id, searchParams)
      : '/mis-cursos';

  const { data, isLoading, error } = useQuery({
    queryKey: ['course-play', isAdminPreview ? 'admin' : 'student', id, item],
    queryFn: () => api<PlayPayload>(playUrl),
    enabled: Boolean(id && item),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    placeholderData: (previous) => previous,
  });

  const player = isH5pPlayerPayload(data?.player) ? data.player : null;
  const itemType = data?.item?.itemType ?? data?.item?.type;
  const isH5p = itemType === 'h5p' || Boolean(player);
  const assessmentContent =
    data?.content?.type === 'assessment' && data.content.assessmentInstanceId
      ? data.content
      : null;

  const completesOnEnter =
    data?.content?.type === 'video' || data?.content?.type === 'pdf' || data?.content?.type === 'pdf_folder';
  useSectionItemCompletion(item, id, !isAdminPreview && completesOnEnter && Boolean(data?.content));

  const sessionContext = useLearningSessionContext();
  const hasPlayableContent = isH5p || Boolean(assessmentContent || data?.content);
  useEffect(() => {
    if (isAdminPreview || !sessionContext || !hasPlayableContent) return;
    sessionContext.setActivityEngaged(true);
    return () => sessionContext.setActivityEngaged(false);
  }, [hasPlayableContent, isAdminPreview, sessionContext]);

  const isPdfDocument = data?.content?.type === 'pdf' || data?.content?.type === 'pdf_folder';

  const activityBody = (
    <>
      {isLoading ? (
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader label="Cargando actividad…" />
        </div>
      ) : error && !data ? (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : player ? (
        <H5pPlayer
          key={player.contentId ?? item}
          player={player}
          sectionItemId={item}
          courseId={id}
          trackCompletion={!isAdminPreview}
        />
      ) : assessmentContent && id && item ? (
        isStaffAssessmentView ? (
          <AssessmentStaffAttemptsView
            assessmentInstanceId={assessmentContent.assessmentInstanceId!}
            backTo={backUrl}
            backLabel="Volver al curso"
            itemTitle={data?.item?.title}
          />
        ) : (
          <AssessmentPlayer
            courseId={id}
            sectionItemId={item}
            assessmentInstanceId={assessmentContent.assessmentInstanceId!}
            isAdminPreview={isAdminPreview}
            backTo={backUrl}
            itemTitle={data?.item?.title}
          />
        )
      ) : data?.content ? (
        <LearningObjectPlayer content={data.content} />
      ) : (
        <article className="rounded-xl border border-dash-outline-variant bg-white p-6 shadow-sm">
          <div className="flex min-h-[40vh] flex-col items-center justify-center text-center">
            <span className="material-symbols-outlined mb-4 text-5xl text-dash-secondary">
              smart_display
            </span>
            <p className="text-dash-on-surface-variant">
              No hay contenido publicado para esta actividad.
            </p>
          </div>
        </article>
      )}
    </>
  );

  return (
    <div
      className={`lms-course-play lms-site-admin-hub-page font-sans${isPdfDocument ? ' lms-course-play--document' : ''}`}
    >
      {assessmentContent ? (
        activityBody
      ) : (
        <div
          className={`lms-site-admin-hub-container${isPdfDocument ? ' lms-course-play__document' : ' py-8 md:py-12'}`}
        >
          <HubDetailPageHeader
            backTo={backUrl}
            backLabel="Volver al curso"
            title={data?.item?.title ?? 'Actividad'}
          />
          <div className={isPdfDocument ? 'lms-course-play__reader' : 'mt-8'}>{activityBody}</div>
        </div>
      )}
    </div>
  );
}
